import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Badge, Form, ListGroup, Button } from 'react-bootstrap';

const getLocalDate = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const Summary = ({ transactions, onCategoryFilterClick, onFilterClick }) => {
  const today = getLocalDate();
  const [projectionDate, setProjectionDate] = useState('');
  const [projectableTransactions, setProjectableTransactions] = useState([]);
  const [showProjectionDetail, setShowProjectionDetail] = useState(true);
  const [commissionMonth, setCommissionMonth] = useState(new Date().toISOString().slice(0, 7));

  // --- Basic Stats Calculations ---
  const nonCompletedTransactions = transactions.filter(t => t.status !== 'completado'); // Changed this line
  const overdueTransactions = nonCompletedTransactions.filter(t => t.date < today); // Changed this line, now uses nonCompletedTransactions
  const dueTodayTransactions = transactions.filter(t => 
    t.date === today && (t.status === 'pendiente' || (t.status && t.status.toLowerCase() === 'por procesar'))
  );

  const porProcesarTransactions = transactions.filter(t => t.status && t.status.toLowerCase() === 'por procesar');
  const totalPorProcesarAmount = porProcesarTransactions.reduce((sum, t) => {
    if (t.type === 'egreso') {
      return sum - t.amount;
    }
    return sum + t.amount;
  }, 0);

  const esperandoOcHesTransactions = transactions.filter(t => t.status && t.status.toLowerCase() === 'esperando oc/hes');
  const totalEsperandoOcHesAmount = esperandoOcHesTransactions.reduce((sum, t) => sum + t.amount, 0);

  const completedTransactions = transactions.filter(t => t.status === 'completado');

  const totalIncome = completedTransactions
    .filter(t => t.type === 'ingreso')
    .reduce((sum, t) => sum + (t.amount + (t.dap_interest || 0) - (t.cc_commission || 0) - (t.penalizaciones_booking || 0)), 0);

  const totalExpenses = completedTransactions
    .filter(t => t.type === 'egreso')
    .reduce((sum, t) => sum + (t.amount - (t.cc_commission || 0)), 0);

  const netBalance = totalIncome - totalExpenses;
  const totalOverdueAmount = overdueTransactions.reduce((sum, t) => sum + t.amount, 0);

  const overdueByCategory = overdueTransactions.reduce((acc, transaction) => {
    const { category } = transaction;
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(transaction);
    return acc;
  }, {});

  const availableMonths = [...new Set(transactions.map(t => t.date.substring(0, 7)))].sort().reverse();

  const totalCommission = transactions
    .filter(t => t.date.substring(0, 7) === commissionMonth)
    .reduce((sum, t) => sum + (t.cc_commission || 0), 0);

  const totalInteresesDAP = transactions
    .filter(t => t.date.substring(0, 7) === commissionMonth)
    .reduce((sum, t) => sum + (t.dap_interest || 0), 0);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(value);
  };

  // --- Interactive Projection Logic ---
  useEffect(() => {
    if (projectionDate) {
      const relevant = transactions
        .filter(t => t.status !== 'completado' && new Date(t.date) <= new Date(projectionDate + 'T00:00:00')) // Filter non-completed with date <= projectionDate
        .map(t => ({ ...t, checked: true })); // Default to checked
      setProjectableTransactions(relevant);
    } else {
      setProjectableTransactions([]); // Clear list if no date is selected
    }
  }, [projectionDate, transactions]);

  const handleProjectionCheckChange = (id) => {
    setProjectableTransactions(prev =>
      prev.map(t => (t.id === id ? { ...t, checked: !t.checked } : t))
    );
  };

  const projectedBalance = projectableTransactions.reduce((balance, t) => {
    if (t.checked) {
      if (t.type === 'ingreso') {
        return balance + (t.amount - (t.tax || 0));
      }
      if (t.type === 'egreso') {
        return balance - (t.amount - (t.tax || 0));
      }
    }
    return balance;
  }, netBalance); // Start calculation from the current net balance

  // --- Render ---
  return (
    <Row className="g-3">
      {/* Main Summary Cards */}
      <Col md={4} lg={3}>
        <Card className="text-center h-100" onClick={() => onFilterClick('all')} style={{ cursor: 'pointer' }}>
          <Card.Body>
            <Card.Title>Total Transacciones</Card.Title>
            <Card.Text className="fs-2">{transactions.length}</Card.Text>
          </Card.Body>
        </Card>
      </Col>
      <Col md={4} lg={3}>
        <Card className="text-center h-100" onClick={() => onFilterClick('pending')} style={{ cursor: 'pointer' }}>
          <Card.Body>
            <Card.Title>Transacciones Pendientes</Card.Title>
            <Card.Text className="fs-2">{nonCompletedTransactions.filter(t => t.status === 'pendiente').length}</Card.Text>
          </Card.Body>
        </Card>
      </Col>
      <Col md={4} lg={3}>
        <Card className="text-center h-100" onClick={() => onFilterClick('overdue')} style={{ cursor: 'pointer' }}>
          <Card.Body>
            <Card.Title>Transacciones Vencidas</Card.Title>
            <Card.Text className="fs-4">
              <Badge bg="danger">
                {overdueTransactions.length} / {formatCurrency(totalOverdueAmount)}
              </Badge>
            </Card.Text>
          </Card.Body>
        </Card>
      </Col>
      <Col md={4} lg={3}>
        <Card className="text-center h-100" onClick={() => onFilterClick('por procesar')} style={{ cursor: 'pointer' }}>
          <Card.Body>
            <Card.Title>Por Procesar</Card.Title>
            <Card.Text className="fs-4">
              <Badge bg="primary">
                {porProcesarTransactions.length} / {formatCurrency(totalPorProcesarAmount)}
              </Badge>
            </Card.Text>
          </Card.Body>
        </Card>
      </Col>
      <Col md={4} lg={3}>
        <Card className="text-center h-100" onClick={() => onFilterClick('esperando oc/hes')} style={{ cursor: 'pointer' }}>
          <Card.Body>
            <Card.Title>Esperando OC/HES</Card.Title>
            <Card.Text className="fs-4">
              <Badge bg="info">
                {esperandoOcHesTransactions.length} / {formatCurrency(totalEsperandoOcHesAmount)}
              </Badge>
            </Card.Text>
          </Card.Body>
        </Card>
      </Col>
      <Col md={6} lg={3}>
        <Card className="text-center h-100" onClick={() => onFilterClick('today')} style={{ cursor: 'pointer' }}>
          <Card.Body>
            <Card.Title>Vencen Hoy</Card.Title>
            <Card.Text className="fs-2">
              <Badge bg="warning" text="dark" className="fs-4">
                {dueTodayTransactions.length}
              </Badge>
            </Card.Text>
          </Card.Body>
        </Card>
      </Col>
      <Col md={6} lg={6}>
        <Card className="text-center h-100" onClick={() => onFilterClick('completado')} style={{ cursor: 'pointer' }}>
          <Card.Body>
            <Card.Title>Ingresos vs Egresos (Completados)</Card.Title>
            <Card.Text className="fs-4">
              <span className="d-block mt-2">Saldo Neto: <Badge bg={netBalance >= 0 ? "primary" : "danger"}>{formatCurrency(netBalance)}</Badge></span>
            </Card.Text>
          </Card.Body>
        </Card>
      </Col>

      {/* Commission Card */}
      <Col md={6} lg={6}>
        <Card className="h-100">
          <Card.Body className="text-center">
            <Card.Title>Comisiones e Intereses (Mes)</Card.Title>
            <Row className="justify-content-center align-items-center">
              <Col md={6}>
                <Form.Group controlId="commissionMonth">
                  <Form.Label>Seleccionar Mes</Form.Label>
                  <Form.Select value={commissionMonth} onChange={(e) => setCommissionMonth(e.target.value)}>
                    {availableMonths.map(month => (
                      <option key={month} value={month}>
                        {new Date(month + '-02').toLocaleString('es-CL', { month: 'long', year: 'numeric' })}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <div className="mt-3">
                  <span className="d-block mb-1">Comisión T/C: <Badge bg="info" text="dark">{formatCurrency(totalCommission)}</Badge></span>
                  <span className="d-block">Intereses DAP: <Badge bg="primary">{formatCurrency(totalInteresesDAP)}</Badge></span>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Col>

      {/* Projection Card */}
      <Col md={12}>
        <Card>
          <Card.Body className="text-center">
            <Card.Title>Proyección de Saldo Neto</Card.Title>
            <Row className="justify-content-center align-items-center">
              <Col md={6}>
                <Form.Group controlId="projectionDate">
                  <Form.Label>Selecciona una fecha futura:</Form.Label>
                  <Form.Control
                    type="date"
                    value={projectionDate}
                    onChange={(e) => setProjectionDate(e.target.value)}
                    min={today}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Card.Text className="fs-4 mt-3">
                  {projectionDate ? (
                    <>
                      <span>Saldo Proyectado:</span>
                      <Badge bg={projectedBalance >= 0 ? "info" : "danger"} text="dark" className="ms-2">
                        {formatCurrency(projectedBalance)}
                      </Badge>
                    </>
                  ) : (
                    <span className="text-muted">Selecciona una fecha para ver la proyección.</span>
                  )}
                </Card.Text>
              </Col>
            </Row>
          </Card.Body>
          {projectableTransactions.length > 0 && (
            <div className="d-grid p-2 border-top">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setShowProjectionDetail(!showProjectionDetail)}
              >
                {showProjectionDetail ? 'Ocultar Detalle' : 'Mostrar Detalle'}
              </Button>
            </div>
          )}
          {showProjectionDetail && projectableTransactions.length > 0 && (
            <ListGroup variant="flush">
              <ListGroup.Item className="bg-light">
                <Row>
                  <Col xs={1} className="fw-bold">Inc.</Col>
                  <Col className="fw-bold">Descripción</Col>
                  <Col md={2} className="text-end fw-bold">Monto</Col>
                </Row>
              </ListGroup.Item>
              {projectableTransactions.map(t => (
                <ListGroup.Item key={t.id} className={t.type === 'ingreso' ? 'text-success' : 'text-danger'}>
                  <Row className="align-items-center">
                    <Col xs={1}>
                      <Form.Check 
                        type="checkbox"
                        checked={t.checked}
                        onChange={() => handleProjectionCheckChange(t.id)}
                      />
                    </Col>
                    <Col>
                      {t.description}
                      <small className="d-block text-muted">Vence: {t.date}</small>
                    </Col>
                    <Col md={2} className="text-end">
                      {t.type === 'egreso' ? '-' : '+'}{formatCurrency(t.amount)}
                    </Col>
                  </Row>
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </Card>
      </Col>

      {/* Overdue by Category Section */}
      {Object.keys(overdueByCategory).length > 0 && (
        <Col xs={12} className="mt-4">
          <h4 className="mb-3">Vencidas por Categoría</h4>
          <Row className="g-3">
            {Object.entries(overdueByCategory).map(([category, items]) => {
              const categoryAmount = items.reduce((sum, item) => sum + item.amount, 0);
              return (
                <Col md={4} key={category}>
                  <Card 
                    className="text-center h-100" 
                    onClick={() => onCategoryFilterClick(category)} 
                    style={{ cursor: 'pointer' }}
                  >
                    <Card.Body>
                      <Card.Title>{category}</Card.Title>
                      <Card.Text className="fs-5">
                        <Badge bg="secondary">
                          {items.length} / {formatCurrency(categoryAmount)}
                        </Badge>
                      </Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </Col>
      )}
    </Row>
  );
};

export default Summary;